// Расчёт срока годности на клиенте — КАНОН, вторая реализация.
//
// Вторая по НЕОБХОДИМОСТИ, а не по недосмотру. Экран печати работает офлайн, и
// `expires_at` попадает на стикер в момент печати, до всякого синка. Месяцы при этом
// складываются календарно от `printed_at` — предвычислить их на сервере нельзя,
// момент печати заранее неизвестен.
//
// Обе реализации подчинены общему набору векторов `docs/wiki/domain/expiry-vectors.json`,
// который прогоняют оба набора тестов. Это единственный механизм, который реально
// держит их вместе: расхождение падает на CI, а не всплывает испорченным продуктом
// на столе. Правка формулы без правки векторов — правка канона втихую.
//
// Канон: `docs/wiki/domain/printjob-lifecycle.md`, раздел «Расчёт срока — КАНОН».

export type ShelfLifeUnit = 'hours' | 'days' | 'months';

const MS_IN_HOUR = 60 * 60 * 1000;
const MS_IN_DAY = 24 * MS_IN_HOUR;

/**
 * Момент истечения срока.
 *
 * @param base момент отсчёта (печать либо своя дата начала)
 * @param unit единица категории
 * @param value длительность, строго больше нуля
 * @param timeZone зона ТОЧКИ — в ней считается календарное сложение месяцев
 */
export function calculateExpiresAt(
  base: Date,
  unit: ShelfLifeUnit,
  value: number,
  timeZone: string,
): Date {
  if (value <= 0) {
    // Нулевой срок дал бы этикетку, просроченную в момент печати.
    throw new RangeError('Длительность срока должна быть больше нуля.');
  }

  switch (unit) {
    case 'hours':
      // Часы и дни — ТОЧНАЯ длительность, а не календарное сложение. Разница видна
      // на переходе через месяц: 30.01 + 3 дн даёт 02.02, а не «3-е число».
      return new Date(base.getTime() + value * MS_IN_HOUR);
    case 'days':
      return new Date(base.getTime() + value * MS_IN_DAY);
    case 'months':
      return addMonthsInZone(base, value, timeZone);
    default: {
      const exhaustive: never = unit;
      throw new RangeError(`Неизвестная единица срока: ${String(exhaustive)}`);
    }
  }
}

/**
 * Календарное сложение месяцев с клампом конца месяца, в зоне точки.
 *
 * Знак «×» для месяцев некорректен: у месяца нет фиксированной длины, и три толкования
 * дают три разные даты на стикере. Канон говорит «тот же день и час в целевом месяце,
 * а если такого дня нет — последний день месяца»: 31.08 + 6 мес = 28.02.
 *
 * Зона несущая, а не косметическая. 31.08 15:00 UTC — это ещё 31 августа в Москве,
 * но уже 1 сентября во Владивостоке: одна и та же печать даёт РАЗНЫЕ базы календарного
 * сложения. Счёт в UTC ошибся бы месяцем на стикере для половины страны — в России
 * 11 часовых поясов.
 */
function addMonthsInZone(base: Date, months: number, timeZone: string): Date {
  const local = zonedParts(base, timeZone);

  const total = local.month - 1 + months;
  const year = local.year + Math.floor(total / 12);
  const month = (((total % 12) + 12) % 12) + 1;
  const day = Math.min(local.day, daysInMonth(year, month));

  return fromZonedParts(
    { year, month, day, hour: local.hour, minute: local.minute, second: local.second },
    timeZone,
  );
}

/**
 * Местные части момента в конкретной зоне. Экспортируется: экран «Своя этикетка»
 * разбирает введённое человеком время в зоне ТОЧКИ (`zonedParts`/`fromZonedParts`),
 * а не в зоне устройства — часы устройства зоной точки не являются (в России 11 поясов).
 */
export type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};
type Parts = ZonedParts;

const FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let cached = FORMATTER_CACHE.get(timeZone);
  if (!cached) {
    // `en-CA` даёт ISO-подобный порядок, но разбор всё равно идёт по частям:
    // строковый парсинг локализованного вывода — источник тихих ошибок.
    cached = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    FORMATTER_CACHE.set(timeZone, cached);
  }
  return cached;
}

/** Разложить момент на местные части в указанной зоне. */
export function zonedParts(date: Date, timeZone: string): Parts {
  const parts = formatter(timeZone).formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const found = parts.find((part) => part.type === type);
    return found ? Number(found.value) : 0;
  };

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    // hourCycle h23 иногда отдаёт «24» для полуночи — приводим к 0, иначе
    // Date.UTC перекинет сутки вперёд.
    hour: read('hour') % 24,
    minute: read('minute'),
    second: read('second'),
  };
}

/**
 * Собрать момент из местных частей зоны.
 *
 * Смещение зоны зависит от самой даты (переход на летнее время в зонах, где он есть),
 * поэтому берётся в два прохода: первое приближение по смещению исходной даты, затем
 * уточнение по смещению получившейся. Без второго прохода сложение через границу
 * перевода часов промахивалось бы на час.
 */
export function fromZonedParts(parts: Parts, timeZone: string): Date {
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );

  let result = new Date(asUtc - zoneOffsetMs(new Date(asUtc), timeZone));
  result = new Date(asUtc - zoneOffsetMs(result, timeZone));
  return result;
}

/** Смещение зоны относительно UTC в миллисекундах на указанный момент. */
function zoneOffsetMs(date: Date, timeZone: string): number {
  const local = zonedParts(date, timeZone);
  const asUtc = Date.UTC(
    local.year,
    local.month - 1,
    local.day,
    local.hour,
    local.minute,
    local.second,
  );
  return asUtc - date.getTime();
}

/** Число дней в месяце. Единственное место клампа на клиенте. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Просрочка — ПРЕДИКАТ, а не статус.
 *
 * В базе три статуса, четвёртого нет и заводить его нельзя: фонового джоба пересчёта
 * не существует, и офлайн-клиент обязан считать тем же правилом, что и сервер.
 */
export function isExpiredAt(expiresAt: Date, now: Date): boolean {
  return expiresAt.getTime() < now.getTime();
}
